import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Wallet, DollarSign,  Heart, Sparkles, CreditCard, Shield, Gift } from "lucide-react";

/**
 * Props del componente LoadBalanceModal
 */
interface LoadBalanceModalProps {
  isOpen: boolean;
  onClose: () => void;
  childName: string;
  childId: string;
}

/**
 * Modal para cargar saldo a un hijo optimizado para dispositivos móviles
 *
 * Proporciona una interfaz intuitiva para que los apoderados
 * puedan cargar saldo a las cuentas de sus hijos.
 * Optimizado para pantallas táctiles con campos grandes.
 */
const LoadBalanceModal: React.FC<LoadBalanceModalProps> = ({
  isOpen,
  onClose,
  childName,
  childId,
}) => {
  const [amount, setAmount] = useState<string>("");
  const [isValid, setIsValid] = useState<boolean>(false);

  /**
   * Valida el monto ingresado
   */
  useEffect(() => {
    const numericAmount = parseFloat(amount);
    setIsValid(numericAmount > 0 && !isNaN(numericAmount));
  }, [amount]);

  /**
   * Formatea el monto como moneda chilena
   */
  const formatAmount = (value: string): string => {
    // Remover caracteres no numéricos excepto punto decimal
    const cleanValue = value.replace(/[^\d.]/g, "");

    // Permitir solo un punto decimal
    const parts = cleanValue.split(".");
    if (parts.length > 2) {
      return parts[0] + "." + parts.slice(1).join("");
    }

    return cleanValue;
  };

  /**
   * Maneja el cambio en el input de monto
   */
  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formattedValue = formatAmount(e.target.value);
    setAmount(formattedValue);
  };

  /**
   * Maneja el envío del formulario
   */
  const handleSubmit = () => {
    if (!isValid) return;

    const numericAmount = parseFloat(amount);

    // Simular inicio del flujo de pago
    console.log(
      `Iniciando flujo de pago de $${numericAmount.toLocaleString(
        "es-CL"
      )} para el hijo ${childName} (ID: ${childId})`
    );

    // Cerrar modal y limpiar formulario
    handleClose();
  };

  /**
   * Maneja el cierre del modal
   */
  const handleClose = () => {
    setAmount("");
    setIsValid(false);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto bg-gradient-to-br from-card/90 to-card/70 backdrop-blur-xl border border-border/50 rounded-3xl shadow-2xl">
        <DialogHeader className="pb-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 bg-gradient-to-br from-primary to-primary/80 rounded-2xl flex items-center justify-center shadow-lg">
              <Wallet className="h-6 w-6 text-primary-foreground" />
            </div>
            <div>
              <DialogTitle className="text-xl sm:text-2xl font-bold text-foreground">
                Cargar Saldo a {childName}
              </DialogTitle>
              <DialogDescription className="text-sm text-muted-foreground mt-1">
                Ingresa el monto que deseas cargar a la cuenta de tu hijo
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Información del hijo mejorada */}
          <Card className="bg-gradient-to-r from-primary/10 to-primary/5 border border-primary/20 rounded-2xl">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-primary/20 rounded-full flex items-center justify-center">
                  <Heart className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <h3 className="font-bold text-foreground">{childName}</h3>
                  <p className="text-xs text-muted-foreground flex items-center gap-1">
                    <Shield className="h-3 w-3" />
                    ID: {childId.slice(0, 8)}...
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Campo de monto mejorado */}
          <div className="space-y-3">
            <Label
              htmlFor="amount"
              className="text-sm font-medium text-foreground flex items-center gap-2"
            >
              <DollarSign className="h-4 w-4 text-muted-foreground" />
              Monto a Cargar
            </Label>
            <div className="relative group">
              <span className="absolute left-4 top-1/2 transform -translate-y-1/2 text-muted-foreground text-lg font-medium">
                $
              </span>
              <Input
                id="amount"
                type="text"
                placeholder="0"
                value={amount}
                onChange={handleAmountChange}
                className="h-14 pl-8 text-lg font-medium bg-background/50 border-border/50 focus:border-primary/50 focus:ring-2 focus:ring-primary/20 rounded-2xl transition-all duration-200 group-hover:border-primary/30"
                autoFocus
              />
            </div>
            {amount && !isValid && (
              <div className="flex items-center gap-2 text-sm text-rose-600 bg-rose-500/10 border border-rose-500/20 rounded-xl p-3">
                <DollarSign className="h-4 w-4" />
                <span>Por favor, ingresa un monto válido mayor a $0</span>
              </div>
            )}
            {amount && isValid && (
              <div className="flex items-center gap-2 text-sm text-emerald-600 bg-emerald-500/10 border border-emerald-500/20 rounded-xl p-3">
                <Sparkles className="h-4 w-4" />
                <span>Monto válido: ${parseFloat(amount).toLocaleString("es-CL")}</span>
              </div>
            )}
          </div>

          {/* Información adicional mejorada */}
          <div className="bg-gradient-to-r from-muted/50 to-muted/30 border border-border/50 rounded-2xl p-4">
            <div className="flex items-center gap-2 mb-3">
              <CreditCard className="h-4 w-4 text-primary" />
              <h4 className="font-medium text-sm text-foreground">
                Información del Pago
              </h4>
            </div>
            <div className="text-sm text-muted-foreground space-y-2">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-emerald-500 rounded-full"></div>
                <span>El saldo se cargará inmediatamente después del pago</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-emerald-500 rounded-full"></div>
                <span>Aceptamos pagos con tarjeta de crédito/débito</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-emerald-500 rounded-full"></div>
                <span>El proceso es seguro y encriptado</span>
              </div>
            </div>
          </div>

          {/* Beneficios adicionales */}
          <div className="bg-gradient-to-r from-primary/5 to-primary/10 border border-primary/20 rounded-2xl p-4">
            <div className="flex items-center gap-2 mb-3">
              <Gift className="h-4 w-4 text-primary" />
              <h4 className="font-medium text-sm text-primary">
                Beneficios para tu Hijo
              </h4>
            </div>
            <div className="text-xs text-muted-foreground space-y-1">
              <p>• Acceso inmediato a productos en la tienda</p>
              <p>• Gestión segura de saldo</p>
              <p>• Historial detallado de transacciones</p>
            </div>
          </div>
        </div>

        <DialogFooter className="flex space-x-3 pt-6">
          <Button
            type="button"
            variant="outline"
            onClick={handleClose}
            className="flex-1 h-12 hover:bg-primary/10 transition-all duration-200 rounded-2xl"
          >
            Cancelar
          </Button>
          <Button
            type="button"
            onClick={handleSubmit}
            disabled={!isValid}
            className="flex-1 h-12 bg-gradient-to-r from-primary to-primary/80 hover:from-primary/90 hover:to-primary/70 transition-all duration-200 shadow-lg hover:shadow-xl rounded-2xl font-medium"
          >
            <Wallet className="h-4 w-4 mr-2" />
            Proceder al Pago
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default LoadBalanceModal;
